import { fromJS } from 'immutable'
import { intersection } from 'lodash-es'
import { useCallback, useContext, useMemo, useRef } from 'react'

import { ActionBar } from './ActionBar'
import { ColumnContext, contactAndMapsPlugins } from './MultiColumnBlock'
import type { ImageEditorRef } from '../../../ImageEditor/ImageEditor'
import { blockTypes } from '../blocks'
import { contentElements } from './ElementInsertionMenu'
import { emptyText } from '../../../editorSidebarBlockTemplates'
import { getPluginTypes } from './MultiColumnBlock.helpers'
import { useTranslate } from '../../../../utils/translate'
import Element from '../Element'

type ColumnProps = Readonly<{
  config: UnknownRecord
  data: ImmutableMap
  index: number
  column: ImmutableMap
  editorView: boolean
  editorMode?: 'view' | 'edit'
  requestModeChange?: (editorMode: 'view' | 'edit') => void
  parentEditorMode: 'view' | 'edit'
  parentRequestModeChange: (editorMode: 'view' | 'edit') => void
  onDataChange: (data: ImmutableMap) => void
  isNested: boolean
}>

export function Column({
  config,
  data,
  index,
  column,
  editorView,
  parentEditorMode,
  parentRequestModeChange,
  onDataChange,
  isNested,
}: ColumnProps) {
  const columnRef = useRef<HTMLDivElement | null>(null)
  const BlockComponent = blockTypes[column.get('type')]

  const columns = data.get('columns')
  const isMultiColumn = columns.size > 1

  // Support both the normal multi-column block data structure
  // (e.g. .type="multi-column", .data.columns[].type="epages.image"),
  // and the "plugin with text below" block data structure
  // (e.g. .type="image-with-text", .data.columns[].image.type="epages.image", .data.columns[].text.type="epages.text")
  const hasTextBelow = data.hasIn(['columns', '0', 'text'])
  const type = hasTextBelow
    ? data
        .getIn(['columns', '0'])
        .keySeq()
        .filterNot((k) => k === '_id' || k === 'text')
        .first()
    : column.get('type')

  const columnContext = useContext(ColumnContext)
  const [addingColumnAtIndex] = columnContext.columnAddState
  const isAddingColumn = addingColumnAtIndex > -1

  const hasReachedMaxColumns =
    columns.size > 3 ||
    fullWidthOnlyPlugins.includes(type) ||
    (columns.size >= 2 && intersection(getPluginTypes(columns), contactAndMapsPlugins).length > 0)

  const shouldShowAddColumnButtons = editorView && !isNested && !hasReachedMaxColumns

  const addColumn = (index: number, block: ImmutableMap, data: ImmutableMap) => {
    if (hasTextBelow) {
      block = fromJS({
        [block.get('type').replace('epages.', '')]: block,
        text: emptyText,
      })
    }
    onDataChange(data.update('columns', (columns: ImmutableList) => columns.insert(index, block)))
  }

  const blockOfSameType = useMemo(() => contentElements[type]?.generate(), [type])

  const [columnModes, setColumnModes] = columnContext.columnModeState
  const handleEditButtonClick = (action: WorkspacePluginEditAction = null) => {
    setColumnModes((c) => ({
      ...c,
      [index]: ['edit', action],
    }))
  }
  const handleRequestModeChange = (editorMode: WorkspacePluginEditorMode, editAction: WorkspacePluginEditAction) => {
    setColumnModes((c) => ({
      ...c,
      [index]: [editorMode, editAction],
    }))
  }

  const columnMode = columnModes[index] || ['view', null]
  const [editorMode, editAction] = columnMode

  // The action bar can change the image, but the editor is code-split and isn't mounted when
  // the action bar fires (it's a full-screen spotlight, so it can't be open at that point).
  // We stash the chosen file here and hand it to the editor as soon as it mounts (EPUI-3681).
  const pendingImageFileRef = useRef<File | null>(null)

  const setImageEditorRef = useCallback((editor: ImageEditorRef | null) => {
    if (editor && pendingImageFileRef.current) {
      const file = pendingImageFileRef.current
      pendingImageFileRef.current = null
      editor.changeImage(file)
    }
  }, [])

  const handleActionBarChangeImage = (file: File) => {
    pendingImageFileRef.current = file
    handleEditButtonClick('editImage')
  }

  return (
    <div ref={columnRef} className={isNested ? undefined : 'dali-grid-column'}>
      {shouldShowAddColumnButtons &&
        (!isAddingColumn || index !== addingColumnAtIndex) &&
        (hasTextBelow ? (
          <PlusButton onClick={() => addColumn(index, blockOfSameType, data)} />
        ) : (
          <AddColumnButton index={index} onNewColumn={addColumn.bind(null, index)} />
        ))}
      {BlockComponent ? (
        <BlockComponent
          config={config}
          editorView={editorView}
          editorMode={editorMode}
          requestModeChange={handleRequestModeChange}
          data={column.get('data')}
          onDataChange={(block: Core.Block) => onDataChange(data.setIn(['columns', index, 'data'], block))}
        />
      ) : hasTextBelow ? (
        <>
          <Element
            config={config}
            editorView={editorView}
            editorMode={editorMode}
            editAction={editAction}
            requestModeChange={handleRequestModeChange}
            isMultiColumn={isMultiColumn}
            element={column.get(type)}
            imageEditorRef={setImageEditorRef}
            onElementChange={(element: ImmutableMap) => onDataChange(data.setIn(['columns', index, type], element))}
          />
          <Element
            config={config}
            editorView={editorView}
            isMultiColumn={isMultiColumn}
            element={column.get('text')}
            onElementChange={(element: ImmutableMap) => onDataChange(data.setIn(['columns', index, 'text'], element))}
          />
        </>
      ) : (
        <Element
          config={config}
          editorView={editorView}
          editorMode={parentEditorMode || editorMode}
          editAction={editAction}
          requestModeChange={parentRequestModeChange || handleRequestModeChange}
          element={column}
          isMultiColumn={isMultiColumn}
          imageEditorRef={setImageEditorRef}
          onElementChange={(element: ImmutableMap) => onDataChange(data.setIn(['columns', index], element))}
        />
      )}
      {editorView && !isNested && (
        <>
          <ActionBar
            index={index}
            data={data}
            element={hasTextBelow ? column.get(type) : column}
            onDataChange={onDataChange}
            onEditButtonClick={handleEditButtonClick}
            onChangeImage={handleActionBarChangeImage}
            columnRef={columnRef}
          />
          <span className="dali-grid-column-selection-border" aria-hidden="true" />
        </>
      )}
      {shouldShowAddColumnButtons &&
        index === columns.size - 1 &&
        (hasTextBelow ? (
          <PlusButton onClick={() => addColumn(index + 1, blockOfSameType, data)} />
        ) : (
          <AddColumnButton index={index + 1} onNewColumn={addColumn.bind(null, index + 1)} />
        ))}
    </div>
  )
}

function AddColumnButton({ index }: Readonly<{ index: number; onNewColumn: (b: ImmutableMap) => void }>) {
  const { columnAddState } = useContext(ColumnContext)
  const [addingColumnAtIndex, setAddingColumnAtIndex] = columnAddState
  if (index === addingColumnAtIndex) return null

  return <PlusButton onClick={() => setAddingColumnAtIndex(index)} />
}

function PlusButton({ onClick }: Readonly<{ onClick: () => void }>) {
  const t = useTranslate('interface', 'components.elementInsertionComponent')

  // defer required for seamless switching to another insertion menu when one is open
  const handleClick = () => setTimeout(onClick, 0)

  return (
    <button title={t('plusButton.label')} className="dali-grid-column-add-button" onClick={handleClick}>
      <span className="visually-hidden">{t('plusButton.label')}</span>
      <span className="vertical-line-top"></span>
      <span className="plus-circle-icon"></span>
      <span className="vertical-line-bottom"></span>
    </button>
  )
}

const fullWidthOnlyPlugins = [
  'ng.category',
  'epages.productslider',
  'epages.contentslider',
  'epages.imagegallery',
  'epages.divider',
  'epages.expander',
  'epages.html',
  'epages.logo',
  'epages.heroimage',
]
